import filter from 'lodash/filter';
import find from 'lodash/find';
import includes from 'lodash/includes';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import { BridgeActionType, BridgePlatform, IBaseBridgeAction, IBridgeAction, IBridgeTrigger } from './types';

export const isOpxpPlatform = (platform?: BridgePlatform[]) => {
  return !!platform && platform.indexOf('opxp') >= 0;
};

export const pickBridgeConfig = (action: IBaseBridgeAction) => action.default || {};

export const pickBridgePlatform = (action: IBaseBridgeAction) => pickBridgeConfig(action).platform;

export const pickBridgeType = (action: IBaseBridgeAction) => pickBridgeConfig(action).type;

export const getBridgePriority = (action: IBaseBridgeAction) => pickBridgeConfig(action).priority;

export const getBridgeCta = (action: IBaseBridgeAction) => pickBridgeConfig(action).cta;

export const getBridgeCtaPayload = (action: IBaseBridgeAction) => pickBridgeConfig(action).ctaPayload;

export const canCloseBridge = (action: IBaseBridgeAction) => pickBridgeConfig(action).showCloseButton;

export const getTriggers = (action: IBaseBridgeAction) => pickBridgeConfig(action).triggers;

export const getTimes = (action: IBaseBridgeAction) => pickBridgeConfig(action).times;

export const getDelay = (action: IBaseBridgeAction) => pickBridgeConfig(action).delay;

export const filterOpxpBridgeActions: (action: IBridgeAction) => boolean = (bridgeAction: IBridgeAction) => {
  const action = pickAction(bridgeAction);
  const isBridge = isBridgeType(pickBridgeType(action));
  if (!isBridge) {
    return false;
  }
  const isOpxp = isOpxpPlatform(pickBridgePlatform(action));
  return isOpxp;
};

// filters incoming actions by platform
export const getOpxpActions = (actions: IBridgeAction[]) => filter(actions, filterOpxpBridgeActions);

// checks if action is of type 'bridge'
export function isBridgeType(types: BridgeActionType[]) {
  return types.indexOf('bridge') >= 0;
}

export const getActionTranslation = (action: IBaseBridgeAction) => action.languages;

export const getNamespace = (action: IBaseBridgeAction, language: string) => {
  const languages = getActionTranslation(action) || {};
  return languages[language] || languages['de'] || {};
};

export const isNamespaceDefined = (action: IBaseBridgeAction, language: string) => {
  const languages = getActionTranslation(action);
  return !!languages && (!!languages[language] || !!language['de']);
};

export const sortBridgeActionsPriority = (actions: IBridgeAction[]) =>
  sortBy(actions, (action: IBridgeAction) => getBridgePriority(pickAction(action)));

export const pickAction = (bridgeAction: IBridgeAction) => bridgeAction.action || {};

export const pickFilterId = (bridgeAction: IBridgeAction) => bridgeAction.filterid || 0;

export const removeAction = (actions: IBridgeAction[], filterId: number) => reject(actions, ['filterid', filterId]);

export const findAction = (actions: IBridgeAction[], filterId: number) => find(actions, ['filterid', filterId]);

export const createNewTrigger = (filterid: number) =>
  ({
    filterid,
    triggeredAt: 0,
    times: 0,
  }) as IBridgeTrigger;

export function isPageInTriggers(page: string, triggers: string[]) {
  return includes(triggers, page);
}

export const doesPageTriggersAction = (page: string) => (bridgeAction: IBridgeAction) => {
  const action = pickAction(bridgeAction);
  // check for triggers
  const triggers = getTriggers(action);
  if (!triggers) {
    return true;
  }
  return isPageInTriggers(page, triggers);
};

export const findActiveActionForPage = (page: string, actions: IBridgeAction[]) => {
  return find(actions, doesPageTriggersAction(page));
};

export const triggerHasParameterTag = (trigger: string) => {
  return trigger.startsWith('param');
};

export const findParameterTrigger = (triggers: string[] | undefined) => find(triggers, triggerHasParameterTag);

export const doesActionHaveParameterRequirement = (bridgeAction: IBridgeAction) => {
  const action = pickAction(bridgeAction);
  const triggers = getTriggers(action);
  return !!findParameterTrigger(triggers);
};

export const getTriggerParameterName = (bridgeAction: IBridgeAction) => {
  const action = pickAction(bridgeAction);
  const triggers = getTriggers(action);
  const paramTrigger = findParameterTrigger(triggers);
  if (!paramTrigger) {
    return;
  }
  const parts = paramTrigger.split(':');
  return parts[1];
};

export const getTriggerParameterValue = (bridgeAction: IBridgeAction) => {
  const action = pickAction(bridgeAction);
  const triggers = getTriggers(action);
  const paramTrigger = findParameterTrigger(triggers);
  if (!paramTrigger) {
    return;
  }
  const parts = paramTrigger.split(':');
  return parts[2];
};

export const isActionTriggeredEnough = (bridgeAction: IBridgeAction, trigger?: IBridgeTrigger) => {
  const action = pickAction(bridgeAction);
  const times = getTimes(action);
  return !!times && !!trigger && trigger.times >= times;
};

export const isActionTriggeredRecently = (bridgeAction: IBridgeAction, trigger?: IBridgeTrigger) => {
  const action = pickAction(bridgeAction);
  const delay = getDelay(action);
  if (!delay || !trigger) {
    // there is no delay set or no trigger was done before - can trigger action right away
    return false;
  }
  const triggeredAt = trigger.triggeredAt;
  const now = Date.now();
  // skip this action as the delay is not yet finished
  return now - triggeredAt < delay * 1000; // delay is in seconds
};
